import { useCallback } from 'react';
import { useReducedMotion, useSharedValue, withTiming } from 'react-native-reanimated';
import { OfferHubCardLayer } from './offer-hub-card-layer';
import { REVIEW_SWIPE_DURATION, SwipeableReviewCard } from 'app/components/swipeable-review-card';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { HubCard, HubCopy, HubEmoji, ItemFace, QuickOffersOverview, hubColors, styles } from '../components';
import { OfferHubCompletion } from './offer-hub-completion';
import { OfferHubTutorial } from './offer-hub-tutorial';

import type { OfferHubState } from '../hooks';

export function OfferHubStage(props: Parameters<typeof OfferHubStageContent>[0]) {
  return (
    <ScrollView
      style={styles.flex}
      contentContainerStyle={[
        styles.content,
        { flexGrow: 1, flex: undefined, minHeight: 380, paddingTop: props.swiping ? 32 : 16 },
      ]}
      scrollEnabled={!props.swiping && !props.tutorialVisible && !props.swipeTutorialSwiping}
      showsVerticalScrollIndicator={false}
      removeClippedSubviews={false}
    >
      <OfferHubStageContent {...props} />
    </ScrollView>
  );
}

function OfferHubStageContent({
  swiping,
  loaded,
  t,
  replayingSwipeTutorial,
  swipeTutorialStep,
  swipeTutorialCard,
  tab,
  focused,
  setSwipeTutorialStep,
  currentItem,
  completed,
  completedLists,
  accepted,
  navigation,
  statistics,
  refreshStatistics,
  pending,
  items,
  swipeCard,
  showError,
  decide,
  theme,
  inProgress,
  round,
  overviewActive,
}: Pick<
  OfferHubState,
  | 'swiping'
  | 'tutorialVisible'
  | 'swipeTutorialSwiping'
  | 'loaded'
  | 't'
  | 'replayingSwipeTutorial'
  | 'swipeTutorialStep'
  | 'swipeTutorialCard'
  | 'tab'
  | 'focused'
  | 'setSwipeTutorialStep'
  | 'currentItem'
  | 'completed'
  | 'completedLists'
  | 'accepted'
  | 'navigation'
  | 'statistics'
  | 'refreshStatistics'
  | 'pending'
  | 'items'
  | 'swipeCard'
  | 'showError'
  | 'decide'
  | 'theme'
  | 'inProgress'
  | 'round'
  | 'overviewActive'
>) {
  const reducedMotion = useReducedMotion();
  const promotingId = useSharedValue<string | null>(null);
  const promotion = useSharedValue(0);
  const nextItemId = items[1]?.id ?? null;
  const promoteNextCard = useCallback(() => {
    'worklet';
    promotingId.set(nextItemId);
    promotion.set(0);
    promotion.set(withTiming(1, { duration: reducedMotion ? 0 : REVIEW_SWIPE_DURATION }));
  }, [nextItemId, promotingId, promotion, reducedMotion]);
  if (!loaded)
    return (
      <View style={styles.center}>
        <ActivityIndicator color={hubColors.teal} accessibilityLabel={t('loading')} />
      </View>
    );
  if (replayingSwipeTutorial)
    return (
      <OfferHubTutorial
        swipeTutorialStep={swipeTutorialStep}
        swipeTutorialCard={swipeTutorialCard}
        t={t}
        tab={tab}
        focused={focused}
        setSwipeTutorialStep={setSwipeTutorialStep}
        currentItem={currentItem}
      />
    );
  if (completed)
    return (
      <OfferHubCompletion
        t={t}
        accepted={accepted}
        navigation={navigation}
        statistics={statistics}
        refreshStatistics={refreshStatistics}
        pending={pending}
        completedLists={completedLists}
      />
    );
  if (swiping && currentItem)
    return (
      <View style={styles.flex}>
        {/* Stable keyed layers let the next card become active without remounting its image. */}
        {items
          .slice(0, 2)
          .reverse()
          .map((item) => {
            const isCurrent = item.id === currentItem.id;
            return (
              <OfferHubCardLayer
                key={item.id}
                current={isCurrent}
                itemId={item.id}
                promotingId={promotingId}
                promotion={promotion}
              >
                <SwipeableReviewCard
                  ref={isCurrent ? swipeCard : undefined}
                  resetKey={item.id}
                  onSwipeStart={isCurrent ? promoteNextCard : undefined}
                  acceptLabel={t('accept')}
                  rejectLabel={t('reject')}
                  canAccept={isCurrent}
                  canReject={isCurrent}
                  disabled={!isCurrent || showError || !focused}
                  onAccept={() => {
                    if (isCurrent) decide('accept');
                  }}
                  onReject={() => {
                    if (isCurrent) decide('reject');
                  }}
                  showOverlays={isCurrent}
                  showShadow={false}
                  overlayVariant="gradient"
                  maxRotation={6}
                  verticalDragFactor={0.08}
                  style={[
                    styles.flex,
                    styles.card,
                    { padding: 0, overflow: 'hidden', borderColor: theme.colors['border.secondary'] },
                  ]}
                >
                  <ItemFace item={item} announce={isCurrent} />
                </SwipeableReviewCard>
              </OfferHubCardLayer>
            );
          })}
      </View>
    );
  if (currentItem)
    return (
      <QuickOffersOverview
        items={items.slice(0, 3)}
        remaining={inProgress ? items.length : undefined}
        total={inProgress ? round?.items.length : undefined}
        active={overviewActive}
      />
    );
  return (
    <HubCard>
      <HubEmoji>👏</HubEmoji>
      <HubCopy title={t('caughtUp')} description={t('caughtUpDescription')} />
    </HubCard>
  );
}
